/**
 * Provenance guards for user_stream_library — streaming views must never
 * receive local-library album/artist ids or non-Qobuz entity keys.
 */

import {
  isLocalLibraryAlbumKey,
  isLocalLibraryArtistKey,
} from '../services/localLibraryMetadata';

const QOBUZ_NUMERIC_ID_RE = /^\d+$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function stripQobuzPrefix(id: string): string {
  return String(id || '').trim().replace(/^qobuz:(album|artist):/i, '');
}

/** True when entity_id is a Qobuz numeric album id (not local hash, UUID, or slug). */
export function isStreamingLibraryAlbumEntityId(entityId?: string | null): boolean {
  const raw = stripQobuzPrefix(String(entityId || ''));
  if (!raw) return false;
  if (isLocalLibraryAlbumKey(raw)) return false;
  if (/^local:/i.test(raw)) return false;
  if (UUID_RE.test(raw)) return false;
  return QOBUZ_NUMERIC_ID_RE.test(raw);
}

/** True when entity_id looks like a Qobuz numeric artist id. */
export function isStreamingLibraryArtistEntityId(entityId?: string | null): boolean {
  const raw = stripQobuzPrefix(String(entityId || ''));
  if (!raw) return false;
  if (isLocalLibraryArtistKey(raw)) return false;
  if (/^local:/i.test(raw)) return false;
  if (UUID_RE.test(raw)) return false;
  if (raw.includes('-') && !QOBUZ_NUMERIC_ID_RE.test(raw)) return false;
  return QOBUZ_NUMERIC_ID_RE.test(raw);
}

const STREAMING_SOURCES = new Set(['qobuz', 'streaming']);

export function isStreamingLibrarySource(source?: string | null): boolean {
  if (!source) return true;
  return STREAMING_SOURCES.has(String(source).toLowerCase());
}

export function isLocalPlaybackContext(opts: {
  source?: string | null;
  file_path?: string | null;
}): boolean {
  if (opts.file_path) return true;
  const src = String(opts.source || '').toLowerCase();
  return src === 'local_library' || src === 'local' || src === 'library';
}

export function isStorableStreamLibraryAlbum(opts: {
  entity_id: string;
  source?: string | null;
  file_path?: string | null;
}): boolean {
  if (isLocalPlaybackContext(opts)) return false;
  if (!isStreamingLibrarySource(opts.source)) return false;
  return isStreamingLibraryAlbumEntityId(opts.entity_id);
}

export function isStorableStreamLibraryArtist(opts: {
  entity_id: string;
  source?: string | null;
}): boolean {
  if (isLocalPlaybackContext(opts)) return false;
  if (!isStreamingLibrarySource(opts.source)) return false;
  return isStreamingLibraryArtistEntityId(opts.entity_id);
}

/** Drop persisted rows that leaked local or non-Qobuz ids into the stream library. */
export function filterStreamLibraryAlbumItems<T extends { entity_type?: string; entity_id?: string }>(
  items: T[],
): T[] {
  return items.filter((item) => {
    if (item.entity_type !== 'album') return true;
    return isStreamingLibraryAlbumEntityId(item.entity_id);
  });
}
